import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/services/api.service';
import type { ApiResponse } from '@/types';
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  Select,
  DatePicker,
  Tag,
  Dropdown,
  Modal,
  Typography,
  Row,
  Col,
  Statistic,
  message,
  Spin,
  Alert,
  Upload
} from 'antd';
import {
  PlusOutlined,
  DownloadOutlined,
  UploadOutlined,
  SearchOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  EyeOutlined,
  CalculatorOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import { openBoqEstimationPrint } from '@/utils/openBoqEstimationPrint';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { listAssignableSalesTeams } from '@/services/salesTeam.service';
import {
  useBOQs,
  useDeleteBOQ,
  useDuplicateBOQ,
  useExportBOQ
} from '../../hooks/useBOQ';
import { useCreateEstimationFromBOQ } from '../../hooks/useMultiItemEstimation';
import { BOQHeader } from '../../services/boq.service';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCurrency } from '@/contexts/CurrencyContext';
import { AED_CURRENCY_CODE } from '@/constants/uaDirhamDisplay';
import { AedDirhamSign } from '@/components/currency/AedDirhamSign';
import { useExcelMigrationIo } from '@/hooks/useExcelMigrationIo';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface OppLinkedEstimation {
  id: string;
  estimation_number?: string;
  estimation_name?: string;
  status?: string;
  is_multi_item_workspace?: boolean;
}

const BOQListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const opportunityIdParam = searchParams.get('opportunity_id') || undefined;
  const { baseCurrency } = useCurrency();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    () => searchParams.get('status') || undefined
  );
  const [teamFilter, setTeamFilter] = useState<string | undefined>(
    () => searchParams.get('sales_team_id') || undefined
  );
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(
    null
  );

  const { data: salesTeams = [] } = useQuery({
    queryKey: ['sales-teams-assignable'],
    queryFn: listAssignableSalesTeams,
  });
  const [multiItemProcessingBoq, setMultiItemProcessingBoq] = useState<BOQHeader | null>(null);

  // Queries and Mutations
  const {
    data: boqsData,
    isLoading,
    refetch
  } = useBOQs({
    search: searchText,
    status: statusFilter,
    fromDate: dateRange?.[0]?.format('YYYY-MM-DD'),
    toDate: dateRange?.[1]?.format('YYYY-MM-DD'),
    sales_team_id: teamFilter || undefined,
    ...(opportunityIdParam ? { opportunity_id: opportunityIdParam } : {}),
  });

  const deleteBOQMutation = useDeleteBOQ();
  const duplicateBOQMutation = useDuplicateBOQ();
  const exportBOQMutation = useExportBOQ();
  const createEstimationFromBOQMutation = useCreateEstimationFromBOQ();

  const { isExcelBusy, downloadTemplate, downloadExport, handleImport: handleMigrationImport } =
    useExcelMigrationIo({
      templateUrl: '/estimations/boq/migration/excel/template',
      exportUrl: '/estimations/boq/migration/excel/export',
      importUrl: '/estimations/boq/migration/excel/import',
      templateFilename: 'boq-migration-import-template.xlsx',
      exportFilenamePrefix: 'boq-migration',
      onImportSuccess: () => refetch(),
      buildExportParams: () => ({
        search: searchText || undefined,
        status: statusFilter,
        fromDate: dateRange?.[0]?.format('YYYY-MM-DD'),
        toDate: dateRange?.[1]?.format('YYYY-MM-DD'),
        sales_team_id: teamFilter || undefined,
        ...(opportunityIdParam ? { opportunity_id: opportunityIdParam } : {}),
      }),
    });

  const { data: oppLinkedEstimations = [], isLoading: oppEstLoading } = useQuery({
    queryKey: ['crm', 'opportunities', opportunityIdParam, 'estimations'],
    queryFn: async () => {
      const res = await api.get<OppLinkedEstimation[]>(
        `/crm/opportunities/${opportunityIdParam}/estimations`
      );
      const d = (res as ApiResponse<OppLinkedEstimation[]>).data;
      return Array.isArray(d) ? d : [];
    },
    enabled: !!opportunityIdParam,
  });

  const oppEstColumns = useMemo(
    () => [
      {
        title: 'Type',
        key: 't',
        width: 120,
        render: (_: unknown, r: OppLinkedEstimation) =>
          r.is_multi_item_workspace ? <Tag color="blue">Multi-item</Tag> : <Tag>CRM</Tag>,
      },
      { title: '#', key: 'n', render: (_: unknown, r: OppLinkedEstimation) => r.estimation_number ?? '—' },
      {
        title: 'Name',
        key: 'nm',
        ellipsis: true,
        render: (_: unknown, r: OppLinkedEstimation) => r.estimation_name ?? '—',
      },
      {
        title: 'Open',
        key: 'o',
        width: 100,
        render: (_: unknown, r: OppLinkedEstimation) => (
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() =>
              navigate(
                r.is_multi_item_workspace
                  ? `/sales/estimations/multi-item/${r.id}`
                  : `/sales/estimations/edit/${r.id}`
              )
            }
          >
            Open
          </Button>
        ),
      },
    ],
    [navigate]
  );

  // Handle both array response and wrapped response
  const boqs: BOQHeader[] = Array.isArray(boqsData) 
    ? boqsData 
    : (boqsData?.data || []);

  // Calculate statistics
  const totalBOQs = boqs.length;
  const totalCostSum = boqs.reduce((sum, boq) => sum + Number(boq.total_amount ?? 0), 0);
  const totalSellingSum = boqs.reduce((sum, boq) => sum + Number(boq.selling_price ?? 0), 0);
  const draftBOQs = boqs.filter(boq => boq.status === 'Draft').length;

  // Handlers
  const handleView = (boqId: string) => {
    navigate(`/growth/estimations/boq/edit/${boqId}`);
  };

  const handleEdit = (boqId: string) => {
    navigate(`/growth/estimations/boq/edit/${boqId}`);
  };

  const handleDelete = (boq: BOQHeader) => {
    Modal.confirm({
      title: 'Delete BOQ',
      content: `Are you sure you want to delete BOQ "${boq.boq_name}"?`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        await deleteBOQMutation.mutateAsync(boq.id);
        refetch();
      }
    });
  };

  const handleDuplicate = async (boq: BOQHeader) => {
    const result = await duplicateBOQMutation.mutateAsync(boq.id);
    // Handle both direct response and wrapped response
    const duplicatedBoq = result?.data || result;
    if (duplicatedBoq?.id) {
      navigate(`/growth/estimations/boq/edit/${duplicatedBoq.id}`);
    }
  };

  const handleExport = async (boq: BOQHeader) => {
    const safeName = (boq.boq_number || boq.boq_name || boq.id || 'boq').replace(/[^a-zA-Z0-9]/g, '_');
    const filename = `${safeName}.xlsx`;
    try {
      await exportBOQMutation.mutateAsync({ boqId: boq.id, filename });
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Export failed');
    }
  };

  const handleImport = () => {
    navigate(
      opportunityIdParam
        ? `/sales/estimations/boq/import?opportunity_id=${opportunityIdParam}`
        : '/sales/estimations/boq/import'
    );
  };

  const handleCreateNew = () => {
    navigate('/growth/estimations/boq/create');
  };

  const handleCreateMultiItemEstimation = async (boq: BOQHeader) => {
    setMultiItemProcessingBoq(boq);
    try {
      const res = await createEstimationFromBOQMutation.mutateAsync({
        boqId: boq.id,
        opportunityId: opportunityIdParam ?? boq.opportunity_id ?? undefined,
      });
      const p = res as { estimationId?: string; id?: string; estimation?: { id?: string } };
      const newEstimationId = p?.estimationId ?? p?.id ?? p?.estimation?.id;
      if (newEstimationId) {
        message.success('Multi-item estimation created from BOQ');
        navigate(`/growth/estimations/multi-item/${newEstimationId}`);
        refetch();
      } else {
        message.error('Failed to create estimation — no ID returned');
      }
    } catch (error: any) {
      message.error(
        error.response?.data?.message || error.message || 'Failed to create estimation from BOQ'
      );
    } finally {
      setMultiItemProcessingBoq(null);
    }
  };

  // Table columns
  const columns: ColumnsType<BOQHeader> = [
    {
      title: 'BOQ Number',
      dataIndex: 'boq_number',
      key: 'boq_number',
      fixed: 'left',
      width: 150,
      render: (text: string, record: BOQHeader) => (
        <Button
          type="link"
          onClick={() => handleView(record.id)}
          style={{ padding: 0, height: 'auto' }}
        >
          {text}
        </Button>
      )
    },
    {
      title: 'BOQ Name',
      dataIndex: 'boq_name',
      key: 'boq_name',
      width: 250,
      ellipsis: true
    },
    {
      title: 'Customer',
      dataIndex: 'customer_name',
      key: 'customer_name',
      width: 200,
      ellipsis: true,
      render: (text: string) => text || '-'
    },
    {
      title: 'Sales team',
      dataIndex: 'sales_team_name',
      key: 'sales_team_name',
      width: 140,
      ellipsis: true,
      render: (text: string) => text || '—',
    },
    {
      title: 'Project',
      dataIndex: 'project_name',
      key: 'project_name',
      width: 200,
      ellipsis: true,
      render: (text: string) => text || '-'
    },
    {
      title: 'Date',
      dataIndex: 'boq_date',
      key: 'boq_date',
      width: 120,
      render: (date: string) => dayjs(date).format('DD MMM YYYY')
    },
    {
      title: 'Cost Total',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 150,
      align: 'right',
      render: (_amount: number, record: BOQHeader) => {
        const cost = Number(record.total_amount ?? 0);
        return (
          <div>
            <div style={{ fontWeight: 'bold' }}>
              {record.currency}{' '}
              {cost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            {Number(record.margin_percentage) > 0 && (
              <div style={{ fontSize: '12px', color: '#52c41a' }}>
                +{record.margin_percentage}% margin
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Selling Total',
      dataIndex: 'selling_price',
      key: 'selling_price',
      width: 150,
      align: 'right',
      render: (_amount: number, record: BOQHeader) => {
        const selling = Number(record.selling_price ?? 0);
        return (
          <div style={{ fontWeight: 'bold' }}>
            {record.currency}{' '}
            {selling.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        );
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const colorMap: Record<string, string> = {
          Draft: 'default',
          'In Review': 'processing',
          Approved: 'success',
          Rejected: 'error',
          Converted: 'purple'
        };
        return <Tag color={colorMap[status] || 'default'}>{status}</Tag>;
      },
      filters: [
        { text: 'Draft', value: 'Draft' },
        { text: 'In Review', value: 'In Review' },
        { text: 'Approved', value: 'Approved' },
        { text: 'Rejected', value: 'Rejected' },
        { text: 'Converted', value: 'Converted' }
      ],
      onFilter: (value: any, record: BOQHeader) => record.status === value
    },
    {
      title: 'Items',
      key: 'items',
      width: 100,
      align: 'center',
      render: (_: any, record: BOQHeader) => (
        <Tag color="blue">{record.lineItems?.length || 0}</Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 100,
      render: (_: any, record: BOQHeader) => {
        const menuItems: any[] = [
          {
            key: 'view',
            icon: <EyeOutlined />,
            label: 'View',
            onClick: () => handleView(record.id)
          },
          {
            key: 'edit',
            icon: <EditOutlined />,
            label: 'Edit',
            onClick: () => handleEdit(record.id),
            disabled: record.status === 'Converted' || record.status === 'In Review'
          },
          {
            key: 'duplicate',
            icon: <CopyOutlined />,
            label: 'Duplicate',
            onClick: () => handleDuplicate(record)
          },
          {
            key: 'export',
            icon: <DownloadOutlined />,
            label: 'Export Excel',
            onClick: () => handleExport(record)
          },
          {
            key: 'print',
            icon: <PrinterOutlined />,
            label: 'Print',
            onClick: () => openBoqEstimationPrint(record.id)
          },
          { type: 'divider' }
        ];

        if (record.status === 'Approved') {
          menuItems.push({
            key: 'create-multi-item',
            icon: <CalculatorOutlined />,
            label: 'Create multi-item estimation',
            onClick: () => handleCreateMultiItemEstimation(record)
          });
        }

        menuItems.push(
          { type: 'divider' },
          {
            key: 'delete',
            icon: <DeleteOutlined />,
            label: 'Delete',
            danger: true,
            onClick: () => handleDelete(record),
            disabled: record.status === 'Converted'
          }
        );

        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      }
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Header */}
          <div>
            <Title level={2}>BOQ Multi-Item Estimations</Title>
            <Typography.Text type="secondary">
              Manage Bill of Quantities for multi-item client orders
            </Typography.Text>
            {opportunityIdParam ? (
              <Alert
                style={{ marginTop: 12 }}
                type="info"
                showIcon
                message="CRM opportunity context"
                description={
                  <>
                    New multi-item estimations and quotations created from this list pass{' '}
                    <Text code>opportunity_id</Text> to the server. Estimations already linked to this
                    opportunity are listed below.
                  </>
                }
              />
            ) : null}
          </div>

          {opportunityIdParam ? (
            <Card title="Estimations linked to this opportunity" loading={oppEstLoading}>
              {oppLinkedEstimations.length === 0 && !oppEstLoading ? (
                <Text type="secondary">
                  None yet. Approve BOQs from the BOQ edit screen when status is In Review, then create a
                  multi-item estimation from an Approved BOQ (actions menu) while this page is open from the
                  opportunity.
                </Text>
              ) : (
                <Table
                  size="small"
                  rowKey="id"
                  dataSource={oppLinkedEstimations}
                  columns={oppEstColumns}
                  pagination={false}
                />
              )}
            </Card>
          ) : null}

          {/* Statistics */}
          <Row gutter={16}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total BOQs"
                  value={totalBOQs}
                  valueStyle={{ color: '#145A54' }}
                  formatter={(val) => Number(val).toLocaleString('en-US')}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total cost"
                  value={totalCostSum}
                  precision={2}
                  prefix={
                    String(baseCurrency?.currency_code || '').toUpperCase() === AED_CURRENCY_CODE ? (
                      <AedDirhamSign />
                    ) : (
                      (baseCurrency?.symbol ?? baseCurrency?.currency_code ?? '')
                    )
                  }
                  valueStyle={{ color: '#145A54' }}
                  formatter={(val) => Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total selling"
                  value={totalSellingSum}
                  precision={2}
                  prefix={
                    String(baseCurrency?.currency_code || '').toUpperCase() === AED_CURRENCY_CODE ? (
                      <AedDirhamSign />
                    ) : (
                      (baseCurrency?.symbol ?? baseCurrency?.currency_code ?? '')
                    )
                  }
                  valueStyle={{ color: '#52c41a' }}
                  formatter={(val) => Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Draft BOQs"
                  value={draftBOQs}
                  valueStyle={{ color: '#faad14' }}
                  formatter={(val) => Number(val).toLocaleString('en-US')}
                />
              </Card>
            </Col>
          </Row>

          <Alert
            type="info"
            showIcon
            message="Quotation conversion moved"
            description="Use BOQ detail/edit or Multi-Item Estimation view/edit to convert to quotation with line-item selection."
          />

          {/* Filters and Actions */}
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col xs={24} md={7}>
              <Input
                placeholder="Search BOQ number, name, or customer..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} md={4}>
              <Select
                placeholder="Filter by status"
                style={{ width: '100%' }}
                value={statusFilter}
                onChange={setStatusFilter}
                allowClear
              >
                <Option value="Draft">Draft</Option>
                <Option value="In Review">In Review</Option>
                <Option value="Approved">Approved</Option>
                <Option value="Rejected">Rejected</Option>
                <Option value="Converted">Converted</Option>
              </Select>
            </Col>
            <Col xs={24} md={5}>
              <Select
                allowClear
                placeholder="Sales team"
                style={{ width: '100%' }}
                value={teamFilter}
                onChange={setTeamFilter}
                options={salesTeams.map((t) => ({ value: t.id, label: t.team_name }))}
              />
            </Col>
            <Col xs={24} md={8}>
              <Space style={{ float: 'right' }}>
                <RangePicker
                  value={dateRange}
                  onChange={setDateRange}
                  format="DD MMM YYYY"
                />
                <Button icon={<DownloadOutlined />} onClick={downloadTemplate} disabled={isExcelBusy}>
                  Migration Template
                </Button>
                <Button icon={<DownloadOutlined />} onClick={downloadExport} disabled={isExcelBusy}>
                  Migration Export
                </Button>
                <Upload accept=".xlsx,.xls" showUploadList={false} customRequest={handleMigrationImport} disabled={isExcelBusy}>
                  <Button icon={<UploadOutlined />} loading={isExcelBusy}>
                    Migration Import
                  </Button>
                </Upload>
                <Button icon={<UploadOutlined />} onClick={handleImport}>
                  Import Excel
                </Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateNew}>
                  New BOQ
                </Button>
              </Space>
            </Col>
          </Row>

          {/* Table */}
          <Table
            columns={columns}
            dataSource={boqs}
            rowKey="id"
            loading={isLoading || !!multiItemProcessingBoq}
            pagination={{
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              defaultPageSize: 20
            }}
            scroll={{ x: 1400 }}
          />
        </Space>
      </Card>

      <Modal
        title="Creating multi-item estimation"
        open={!!multiItemProcessingBoq}
        footer={null}
        closable={false}
        maskClosable={false}
        keyboard={false}
        centered
        width={440}
      >
        <div style={{ textAlign: 'center', padding: '16px 8px 8px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 20 }}>
            <Text strong>
              Building estimation from {multiItemProcessingBoq?.boq_number}
              {multiItemProcessingBoq?.boq_name
                ? ` — ${multiItemProcessingBoq.boq_name}`
                : ''}
            </Text>
          </div>
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">Copying categories and line items. Please keep this window open.</Text>
          </div>
        </div>
      </Modal>

    </div>
  );
};

export default BOQListPage;

